/* eslint-disable @typescript-eslint/ban-ts-comment */

import type { ReactNode } from 'react';
import type { RedirectHref } from '@zg-rentals/auth-react';
import type { PopupLoginOptions, RedirectLoginOptions } from '@zillow/auth-core';

// @ts-ignore: local package uses declaration files, ignore for external consumers
import { getZillowWebLoginURL } from '@zillow/auth-core/dist/es/zoam-web-sdk/utils/getZillowWebLoginURL';
// @ts-ignore: local package uses declaration files, ignore for external consumers
import zillowWebLogin from '@zillow/auth-core/dist/es/zoam-web-sdk/core/zillowWebLogin';
import type { ZillowLoginBaseOptions } from '.';

export function resolveAuthBaseUrl(raw = '/') {
  if (!raw.startsWith('/') || typeof window === 'undefined') return raw;
  return raw === '/' ? window.location.origin : `${window.location.origin}${raw}`;
}

export function partialUrlEncode(str: string) {
  return str.replaceAll('%', '%25').replaceAll('&', '%26').replaceAll('?', '%3F').replaceAll('#', '%23');
}

export type ZillowRedirectLoginOptions = ZillowLoginBaseOptions &
  Omit<RedirectLoginOptions, 'loginMode' | 'trackingConfig' | 'envConfig' | 'redirectUri'> & {
    /** Base URL for the login flow (defaults to https://www.zillow.com if undefined) */
    baseURL?: string;

    /** Optional: path to redirect to after login (defaults to location.pathname) */
    redirectPath?: string;

    /**
     * Optional: forward prefix, use for local development to support redirecting back to localhost dev server
     * @note only works for serverless apps; SSR apps still need to use `rjs-ext` to login and copy cookies to localhost
     */
    forwardPrefix?: string;
  };

/**
 * Returns a `redirectHref` callback for use with `Auth.Boundary` on the
 * client. The callback builds a Zillow login URL from the current page path
 * and auth query params.
 *
 * @note `forwardPrefix` only supported for serverless apps
 *
 * @example
 * ```typescript
 * <Auth.Boundary redirectHref={zillowLoginRedirectHref({ entryPoint: 'example-web', baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL })}>
 *   <p>Hello logged in user</p>
 * </Auth.Boundary>
 * ```
 */
export function zillowLoginRedirectHref({
  baseURL,
  forwardPrefix,
  redirectPath,
  ...options
}: ZillowRedirectLoginOptions): RedirectHref {
  const resolvedBaseURL = resolveAuthBaseUrl(baseURL);

  return (_href: string, params: URLSearchParams) => {
    redirectPath ||= `${location.pathname}?${params}`;

    return getZillowWebLoginURL({
      ...options,
      baseURL: resolvedBaseURL,
      redirectUri: forwardPrefix
        ? `${forwardPrefix}?protocol=https${location.port ? `&port=${location.port}` : ''}&path=${partialUrlEncode(
            redirectPath,
          )}`
        : redirectPath,
    });
  };
}

/**
 * Triggers a Zillow web login via full-page redirect. Use this for standalone
 * login buttons outside of an `Auth.Boundary`.
 *
 * @note use this function in an `onClick` handler or `useEffect` directly since it produces side-effects
 * @note `forwardPrefix` only supported for serverless apps
 *
 * @example
 * ```typescript
 * <button onClick={() => zillowRedirectLogin({ baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL, entryPoint: 'example-web' })}>
 *   Login
 * </button>
 * ```
 *
 * @example
 * ```typescript
 * useEffect(() => {
 *   zillowRedirectLogin({ baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL, entryPoint: 'example-web' });
 * }, []);
 * ```
 */
export async function zillowRedirectLogin({
  baseURL,
  entryPoint,
  forwardPrefix,
  redirectPath,
  ...options
}: ZillowRedirectLoginOptions) {
  const resolvedBaseURL = resolveAuthBaseUrl(baseURL);

  redirectPath ||= location.pathname;

  const redirectUri = forwardPrefix
    ? `${forwardPrefix}?protocol=https${
        typeof window !== 'undefined' && window.location.port ? `&port=${window.location.port}` : ''
      }&path=${partialUrlEncode(redirectPath)}`
    : redirectPath;

  return zillowWebLogin({
    ...options,
    envConfig: { baseURLOverride: resolvedBaseURL },
    loginMode: 'redirect',
    redirectUri,
    trackingConfig: { entryPoint },
  });
}

export type ZillowPopupLoginOptions = ZillowLoginBaseOptions &
  Omit<PopupLoginOptions, 'loginMode' | 'trackingConfig' | 'envConfig'> & {
    /** Base URL for the login flow (defaults to https://www.zillow.com if undefined) */
    baseURL?: string;
  };

/**
 * Triggers a Zillow web login using popup mode.
 *
 * @note use this function in an `onClick` handler or `useEffect` directly since it produces side-effects
 * @note due to ZOAM limitations this does not work well in local development; use `zillowRedirectLogin` instead (if possible)
 *
 * @example
 * ```typescript
 * <button onClick={() => zillowPopupLogin({ baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL, entryPoint: 'example-web' })}>
 *   Login
 * </button>
 * ```
 *
 * @example
 * ```typescript
 * useEffect(() => {
 *   zillowPopupLogin({ baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL, entryPoint: 'example-web' });
 * }, []);
 * ```
 */
export async function zillowPopupLogin({ baseURL, entryPoint, ...options }: ZillowPopupLoginOptions) {
  const resolvedBaseURL = resolveAuthBaseUrl(baseURL);

  return zillowWebLogin({
    ...options,
    envConfig: { baseURLOverride: resolvedBaseURL },
    loginMode: 'popup',
    trackingConfig: { entryPoint },
  });
}

const DEFAULT_MFA_RETRY_PARAM = 'mr';
const DEFAULT_MAX_MFA_RETRIES = 3;

type MfaRetryHandlerOptions<U = unknown> = {
  /** User DTO; retry only fires when `isInitialMultiFactorAuthenticationRequired` is true. */
  user?: (U & { isInitialMultiFactorAuthenticationRequired?: boolean }) | null;
  /** The same `redirectHref` passed to `Auth.Boundary`; invoked to rebuild the login URL with the bumped counter. */
  redirectHref: RedirectHref;
  /** Rendered when retry does not apply (server, MFA not required, or max retries reached). Mirrors the `onUnauthorized` prop shape. */
  fallback: ReactNode | ((user: U) => ReactNode);
  /** Rendered while `window.location.href` is being assigned so React does not flash the fallback. */
  redirecting: ReactNode;
  /** Override the URL param used to track retry attempts (default `'mr'`). */
  retryParam?: string;
  /** Override the max retry count before falling through (default `3`). */
  maxRetries?: number;
};

/**
 * Returns an `onUnauthorized` callback for `Auth.Boundary` that retries the
 * MFA step-up redirect when the user returned from login but still needs
 * initial MFA. `Auth.Boundary`'s `ap=x` loop guard would otherwise short-
 * circuit the redirect; this helper bumps `mr=<n>` (clearing `ap`) and
 * re-invokes `redirectHref` up to `maxRetries` times before falling through.
 */
export function buildMfaStepUpRetryHandler<U = unknown>({
  user,
  redirectHref,
  fallback,
  redirecting,
  retryParam = DEFAULT_MFA_RETRY_PARAM,
  maxRetries = DEFAULT_MAX_MFA_RETRIES,
}: MfaRetryHandlerOptions<U>): (unauthorizedUser: U) => ReactNode {
  const resolveFallback = (unauthorizedUser: U) =>
    typeof fallback === 'function' ? fallback(unauthorizedUser) : fallback;

  return (unauthorizedUser: U) => {
    if (typeof window === 'undefined' || !user?.isInitialMultiFactorAuthenticationRequired) {
      return resolveFallback(unauthorizedUser);
    }

    const params = new URLSearchParams(window.location.search);
    const attempt = Number(params.get(retryParam) ?? '0');
    if (attempt >= maxRetries) {
      return resolveFallback(unauthorizedUser);
    }

    params.set(retryParam, String(attempt + 1));
    params.delete('ap');

    const href =
      typeof redirectHref === 'function' ? redirectHref(window.location.href.split('?')[0], params) : redirectHref;
    window.location.href = href;
    return redirecting;
  };
}
